/**
 * O que faz: exibe campanha, conjunto e anúncio identificados para as entradas de um lead.
 * Onde usa: informações do chat e modais de detalhes do Pipeline/Contatos.
 * Conexões: useLeadMetaAdAttributions e componentes compartilhados Card/Button/Select.
 * Tabelas: somente leitura via RPC; nunca edita a atribuição nem consulta a Meta no navegador.
 * Data: 30/08/2026 (07/10/2026: modo semMoldura para virar seção da aba Origem na ficha do chat).
 * Direção visual: herdar superfícies neutras, Inter e esmeralda do CRM; nomes primeiro,
 * IDs sob demanda; estados explícitos; uma coluna que também funciona na lateral do chat.
 */
import { useId, useState } from 'react';
import { CheckCircle2, ChevronDown, Clock3, ExternalLink, Info, Megaphone, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useLeadMetaAdAttributions, type LeadMetaAdAttribution } from '@/hooks/useLeadMetaAdAttributions';
import { SecaoFichaLead } from '@/components/chat/SecaoFichaLead';

interface OrigemAnuncioLeadProps {
  clinicaId: string | null | undefined;
  leadId: string;
  ativo?: boolean;
  /** Sem cartão: vira uma seção da ficha lateral do chat (rótulo curto + botão de atualizar). */
  semMoldura?: boolean;
}

const formatadorData = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function formatarData(valor: string | null) {
  // Datas inválidas de registros antigos não podem derrubar os detalhes do lead.
  if (!valor || Number.isNaN(new Date(valor).getTime())) return 'Data não informada';
  return formatadorData.format(new Date(valor));
}

function obterUrlAnuncio(valor: string | null) {
  // O payload é externo: somente HTTP(S) sem credenciais pode virar um link clicável.
  // Protocolos executáveis, URLs relativas e endereços inválidos ficam sem ação na tela.
  try {
    const url = new URL(valor ?? '');
    return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url.href : null;
  } catch {
    return null;
  }
}

const estados: Record<LeadMetaAdAttribution['meta_match_status'], { rotulo: string; descricao: string }> = {
  matched: { rotulo: 'Identificado pela Meta', descricao: 'Nomes registrados na última identificação.' },
  pending: {
    rotulo: 'Identificação pendente',
    descricao: 'A referência chegou pelo WhatsApp. A identificação de campanha, conjunto e anúncio ainda está pendente.',
  },
  unmatched: {
    rotulo: 'Anúncio não localizado',
    descricao: 'Não foi possível relacionar a referência às contas Meta conectadas. O registro recebido foi preservado.',
  },
  error: {
    rotulo: 'Identificação indisponível',
    descricao: 'A identificação não foi concluída. Confira a conexão da conta em Configurações → Meta Ads.',
  },
  not_available: {
    rotulo: 'Sem identificação Meta',
    descricao: 'Há uma entrada por anúncio, mas ainda não há identificação confirmada de campanha e conjunto.',
  },
};

function DetalhesAtribuicao({ atribuicao }: { atribuicao: LeadMetaAdAttribution }) {
  const identificada = atribuicao.meta_match_status === 'matched';
  const campos = [
    ['ID recebido no WhatsApp', atribuicao.referencia_anuncio_origem],
    // Só apresenta IDs Meta como confirmados quando o worker validou a correspondência.
    ...(identificada ? [
      ['ID do anúncio', atribuicao.meta_ad_id],
      ['ID do conjunto', atribuicao.meta_adset_id],
      ['ID da campanha', atribuicao.meta_campaign_id],
      ['ID da conta de anúncios', atribuicao.meta_ad_account_id],
    ] : []),
  ];

  return (
    <details className="group border-t border-border pt-1">
      <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-2 rounded-lg text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        Ver IDs e detalhes
        <ChevronDown aria-hidden="true" className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180 motion-reduce:transition-none" />
      </summary>
      <dl className="space-y-3 pb-2 pt-1">
        {campos.map(([rotulo, valor]) => valor ? (
          <div key={rotulo} className="min-w-0 space-y-1">
            <dt className="text-xs text-foreground/70">{rotulo}</dt>
            <dd className="select-text break-all font-mono text-xs leading-relaxed">{valor}</dd>
          </div>
        ) : null)}
        <div className="space-y-1">
          <dt className="text-xs text-foreground/70">Registro recebido</dt>
          <dd className="text-xs tabular-nums">{formatarData(atribuicao.entrada_em || atribuicao.criado_em)}</dd>
        </div>
        {identificada && atribuicao.meta_resolved_at && (
          <div className="space-y-1">
            <dt className="text-xs text-foreground/70">Identificado em</dt>
            <dd className="text-xs tabular-nums">{formatarData(atribuicao.meta_resolved_at)}</dd>
          </div>
        )}
      </dl>
    </details>
  );
}

function ConteudoOrigemAnuncio({ atribuicoes }: { atribuicoes: LeadMetaAdAttribution[] }) {
  const seletorId = useId();
  const [atribuicaoSelecionadaId, setAtribuicaoSelecionadaId] = useState<string | null>(null);
  // A entrada mais recente é o padrão; entradas antigas ficam acessíveis sem misturar campanhas.
  const atribuicao = atribuicoes.find((item) => item.id === atribuicaoSelecionadaId) ?? atribuicoes[0];
  const identificada = atribuicao.meta_match_status === 'matched';
  const estado = estados[atribuicao.meta_match_status];
  const IconeEstado = identificada ? CheckCircle2 : atribuicao.meta_match_status === 'pending' ? Clock3 : Info;
  const urlAnuncio = obterUrlAnuncio(atribuicao.url_anuncio);
  const nomes = [
    ['Campanha', atribuicao.meta_campaign_name],
    ['Conjunto', atribuicao.meta_adset_name],
    ['Anúncio', atribuicao.meta_ad_name],
  ];

  return (
    <div className="min-w-0 space-y-4">
      {atribuicoes.length > 1 && (
        <div className="space-y-2">
          <Label htmlFor={seletorId} className="text-xs text-foreground/70">Entradas por anúncio</Label>
          <Select value={atribuicao.id} onValueChange={(id) => setAtribuicaoSelecionadaId(id === atribuicoes[0].id ? null : id)}>
            <SelectTrigger id={seletorId} className="min-h-11 min-w-0 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-2rem)]">
              {atribuicoes.map((item, indice) => (
                <SelectItem key={item.id} value={item.id} className="min-h-11 text-xs">
                  {indice === 0 ? 'Mais recente · ' : ''}{formatarData(item.entrada_em || item.criado_em)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {atribuicoes.length === 20 && <p className="text-xs text-foreground/70">Exibindo as 20 entradas mais recentes.</p>}
        </div>
      )}

      <div className="space-y-2" role="status">
        <p className={`flex items-center gap-2 text-sm font-medium ${identificada ? 'text-accent-foreground' : 'text-foreground'}`}>
          <IconeEstado aria-hidden="true" className="h-4 w-4 shrink-0" />
          {estado.rotulo}
        </p>
        <p className="text-xs leading-relaxed text-foreground/70">{estado.descricao}</p>
      </div>

      {identificada ? (
        <dl className="space-y-4">
          {nomes.map(([rotulo, nome]) => (
            <div key={rotulo} className="min-w-0 space-y-1">
              <dt className="text-xs text-foreground/70">{rotulo}</dt>
              <dd className="break-words text-sm font-medium leading-relaxed [overflow-wrap:anywhere]">
                {nome || 'Nome indisponível'}
              </dd>
            </div>
          ))}
        </dl>
      ) : atribuicao.titulo_anuncio ? (
        <div className="space-y-1">
          {/* O título do payload não é o nome interno confirmado do anúncio na conta Meta. */}
          <p className="text-xs text-foreground/70">Título recebido na conversa</p>
          <p className="break-words text-sm leading-relaxed [overflow-wrap:anywhere]">{atribuicao.titulo_anuncio}</p>
        </div>
      ) : null}

      {urlAnuncio && (
        <Button asChild variant="outline" className="min-h-11 w-full transition-colors motion-reduce:transition-none">
          <a href={urlAnuncio} target="_blank" rel="noopener noreferrer">
            Abrir anúncio <ExternalLink aria-hidden="true" className="h-4 w-4" />
            <span className="sr-only"> em nova aba</span>
          </a>
        </Button>
      )}
      <DetalhesAtribuicao key={atribuicao.id} atribuicao={atribuicao} />
    </div>
  );
}

export function OrigemAnuncioLead({ clinicaId, leadId, ativo = true, semMoldura = false }: OrigemAnuncioLeadProps) {
  const tituloId = useId();
  const consulta = useLeadMetaAdAttributions(clinicaId, leadId, ativo);
  const atribuicoes = consulta.data ?? [];

  // A lateral permanece montada quando recolhida; não deixa controles ocultos ou polling ativos.
  if (!ativo) return null;

  const botaoAtualizar = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={semMoldura ? 'h-7 w-7 shrink-0 rounded-full transition-colors motion-reduce:transition-none [&_svg]:size-3.5' : 'h-11 w-11 shrink-0 transition-colors motion-reduce:transition-none'}
      aria-label="Atualizar origem do anúncio"
      title="Atualizar origem do anúncio"
      disabled={!consulta.contextoValido || consulta.isFetching}
      onClick={() => void consulta.refetch()}
    >
      <RefreshCw aria-hidden="true" className={consulta.isFetching ? 'animate-spin motion-reduce:animate-none' : ''} />
    </Button>
  );

  const conteudo = (
      <div className="space-y-3">
        {!consulta.contextoValido ? (
          <p role="alert" className="text-sm leading-relaxed">Abra novamente os detalhes deste lead para consultar a origem.</p>
        ) : consulta.isPending && consulta.isPaused ? (
          <p role="status" className="text-sm leading-relaxed">Sem conexão. A origem será carregada quando você ficar online.</p>
        ) : consulta.isPending ? (
          <div role="status" className="space-y-3">
            <span className="sr-only">Carregando origem do anúncio...</span>
            <Skeleton className="h-4 w-3/4 motion-reduce:animate-none" />
            <Skeleton className="h-12 w-full motion-reduce:animate-none" />
            <Skeleton className="h-12 w-full motion-reduce:animate-none" />
          </div>
        ) : consulta.isError && atribuicoes.length === 0 ? (
          <p role="alert" className="text-sm leading-relaxed">Não foi possível carregar a origem do anúncio. Use o botão de atualizar para tentar novamente.</p>
        ) : atribuicoes.length === 0 ? (
          <div className="space-y-1">
            <p className="text-sm font-medium">Nenhuma origem de anúncio registrada</p>
            <p className="text-xs leading-relaxed text-foreground/70">Quando o WhatsApp enviar a referência de um anúncio, ela aparecerá aqui.</p>
          </div>
        ) : (
          <>
            {consulta.isError && <p role="alert" className="text-xs leading-relaxed">Não foi possível atualizar. Exibindo os últimos dados carregados.</p>}
            {/* Trocar de lead reinicia a seleção do histórico, sem carregar uma origem do contato anterior. */}
            <ConteudoOrigemAnuncio key={`${clinicaId}:${leadId}`} atribuicoes={atribuicoes} />
          </>
        )}
      </div>
  );

  if (semMoldura) {
    return (
      <SecaoFichaLead titulo="Anúncio da Meta" icone={Megaphone} acao={botaoAtualizar}>
        {conteudo}
      </SecaoFichaLead>
    );
  }

  return (
    <Card role="region" aria-labelledby={tituloId} className="min-w-0 rounded-2xl shadow-none">
      <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 p-4 pb-3">
        <CardTitle id={tituloId} className="flex min-w-0 items-center gap-2 text-sm font-semibold leading-snug tracking-normal">
          <Megaphone aria-hidden="true" className="h-4 w-4 shrink-0 text-accent-foreground" />
          Origem do anúncio
        </CardTitle>
        {botaoAtualizar}
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">{conteudo}</CardContent>
    </Card>
  );
}
